"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildJVMCreateAssetTransaction = exports.buildJVMImportTransaction = exports.buildJVMExportTransaction = exports.buildJVMBaseTransaction = void 0;
const utils_1 = require("../../utils");
const builder_1 = require("../builder");
const input_1 = require("../input");
const transaction_1 = require("../transaction");
const types_1 = require("../types");
const transaction_2 = require("./transaction");
function buildJVMBaseTransaction(userInputs, utxoSet, signersAddresses, fee, changeAddress, networkId, memo = '') {
    if (userInputs.length < 1) {
        throw new utils_1.InputError('user inputs cannot be empty');
    }
    const sourceId = userInputs[0].sourceChain.id;
    for (const input of userInputs) {
        if (input.sourceChain.id !== sourceId || input.destinationChain.id !== sourceId) {
            throw new utils_1.InputError('jvm base transaction cannot have different source/destination chain user inputs');
        }
    }
    const feeData = new transaction_1.TransactionFee(userInputs[0].sourceChain, fee);
    const inputs = (0, builder_1.buildTransactionInputs)(userInputs, utxoSet, signersAddresses, [feeData]);
    const outputs = (0, builder_1.buildTransactionOutputs)(userInputs, inputs, feeData, changeAddress);
    return new transaction_2.JVMBaseTransaction(networkId, new types_1.BlockchainId(sourceId), outputs, inputs, memo);
}
exports.buildJVMBaseTransaction = buildJVMBaseTransaction;
function buildJVMExportTransaction(userInputs, utxoSet, signersAddresses, exportAddress, sourceFee, destinationFee, changeAddress, networkId, memo = '') {
    if (userInputs.length < 1) {
        throw new utils_1.InputError('user inputs cannot be empty');
    }
    const sourceId = userInputs[0].sourceChain.id;
    const destinationId = userInputs[0].destinationChain.id;
    for (const input of userInputs) {
        if (input.sourceChain.id !== sourceId || input.destinationChain.id !== destinationId) {
            throw new utils_1.InputError('jvm export transaction cannot have different source or destination chain user inputs');
        }
        if (input.sourceChain.id === input.destinationChain.id) {
            throw new utils_1.InputError('jvm export transaction cannot have the same chain as source and destination user inputs');
        }
    }
    const sourceFeeData = new transaction_1.TransactionFee(userInputs[0].sourceChain, sourceFee);
    const destinationFeeData = new transaction_1.TransactionFee(userInputs[0].destinationChain, destinationFee);
    const fees = [sourceFeeData, destinationFeeData];
    const inputs = (0, builder_1.buildTransactionInputs)(userInputs, utxoSet, signersAddresses, fees);
    // fixed user inputs with a defined export address to import it later
    const fixedUserInputs = [];
    for (const input of userInputs) {
        fixedUserInputs.push(new input_1.UserInput(input.assetId, input.sourceChain, input.amount, [exportAddress], 1, input.destinationChain));
    }
    if (destinationFeeData.amount > BigInt(0)) {
        // adding fees as user input to be able to export it
        fixedUserInputs.push(new input_1.UserInput(destinationFeeData.assetId, userInputs[0].sourceChain, destinationFeeData.amount, [exportAddress], 1, userInputs[0].destinationChain));
    }
    const outputs = (0, builder_1.buildTransactionOutputs)(fixedUserInputs, inputs, sourceFeeData, changeAddress);
    const exportedOutputs = [];
    const changeOutputs = [];
    for (const output of outputs) {
        if (!output.isChange) {
            exportedOutputs.push(output);
        }
        else {
            changeOutputs.push(output);
        }
    }
    return new transaction_2.JVMExportTransaction(networkId, new types_1.BlockchainId(sourceId), changeOutputs, inputs, memo, new types_1.BlockchainId(destinationId), exportedOutputs);
}
exports.buildJVMExportTransaction = buildJVMExportTransaction;
function buildJVMImportTransaction(userInputs, utxoSet, signersAddresses, fee, changeAddress, networkId, memo = '') {
    if (userInputs.length < 1) {
        throw new utils_1.InputError('user inputs cannot be empty');
    }
    const sourceId = userInputs[0].sourceChain.id;
    const destinationId = userInputs[0].destinationChain.id;
    for (const input of userInputs) {
        if (input.sourceChain.id !== sourceId || input.destinationChain.id !== destinationId) {
            throw new utils_1.InputError('jvm import transaction cannot have different source or destination chain user inputs');
        }
        if (input.sourceChain.id === input.destinationChain.id) {
            throw new utils_1.InputError('jvm import transaction cannot have the same chain as source and destination user inputs');
        }
    }
    const feeData = new transaction_1.TransactionFee(userInputs[0].destinationChain, fee);
    const inputs = [];
    const importedInputs = [];
    for (const input of (0, builder_1.buildTransactionInputs)(userInputs, utxoSet, signersAddresses, [feeData])) {
        if (input.input.utxo === undefined) {
            throw new utils_1.InputError('input cannot use read only utxo');
        }
        const utxo = input.input.utxo;
        if (utxo.sourceChain === undefined) {
            inputs.push(input);
        }
        else {
            importedInputs.push(input);
        }
    }
    const outputs = (0, builder_1.buildTransactionOutputs)(userInputs, inputs.concat(importedInputs), feeData, changeAddress);
    return new transaction_2.JVMImportTransaction(networkId, new types_1.BlockchainId(destinationId), outputs, inputs, memo, new types_1.BlockchainId(sourceId), importedInputs);
}
exports.buildJVMImportTransaction = buildJVMImportTransaction;
function buildJVMCreateAssetTransaction(utxoSet, signersAddresses, fee, chain, name, symbol, denomination, initialStates, changeAddress, networkId, memo = '') {
    if (initialStates.length < 1) {
        throw new utils_1.InputError('initial states cannot be empty');
    }
    const feeData = new transaction_1.TransactionFee(chain, fee);
    const inputs = (0, builder_1.buildTransactionInputs)([], utxoSet, signersAddresses, [feeData]);
    const outputs = (0, builder_1.buildTransactionOutputs)([], inputs, feeData, changeAddress);
    return new transaction_2.CreateAssetTransaction(networkId, new types_1.BlockchainId(chain.id), outputs, inputs, memo, name, symbol, denomination, initialStates);
}
exports.buildJVMCreateAssetTransaction = buildJVMCreateAssetTransaction;
